import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Reports() {
  const [data, setData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", department_id: "", assigned_to: "", status: "", priority: "" });

  const load = async (f = filters) => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    try {
      const res = await api.get(`/reports/summary?${q.toString()}`);
      setData(res.data);
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (report) => {
    try {
      const res = await api.get(`/reports/export?report=${report}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report}_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const o = data?.overall;

  return (
    <div className="animate-fade-in space-y-5" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Insights</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Task Reports</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="reports-export-tasks" onClick={() => download("tasks")}>
            <Download className="mr-1.5 h-4 w-4" /> Tasks CSV
          </Button>
          <Button variant="outline" data-testid="reports-export-employees" onClick={() => download("employees")}>
            <Download className="mr-1.5 h-4 w-4" /> Employees CSV
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Input data-testid="report-filter-date-from" type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
          <Input data-testid="report-filter-date-to" type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
          <Select value={filters.department_id || "all"} onValueChange={(v) => set("department_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.assigned_to || "all"} onValueChange={(v) => set("assigned_to", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => set("status", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {["new", "started", "in_process", "under_review", "approved", "cancelled"].map((s) => (
                <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="report-apply-filters" onClick={() => load()} className="bg-primary hover:bg-[#1E3A8A]">Apply Filters</Button>
        </div>
      </div>

      {o && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {[
            ["Tasks Created", o.total], ["Completed", o.approved], ["Pending", o.total - o.approved - o.cancelled],
            ["Overdue", o.overdue], ["Completion Rate", `${o.completion_rate}%`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{value}</div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <Tabs defaultValue="employees">
          <TabsList>
            <TabsTrigger value="employees" data-testid="report-tab-employees">Employee Performance</TabsTrigger>
            <TabsTrigger value="departments" data-testid="report-tab-departments">Department Performance</TabsTrigger>
            <TabsTrigger value="managers" data-testid="report-tab-managers">Manager Performance</TabsTrigger>
          </TabsList>
          <TabsContent value="employees">
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3 font-semibold">Employee</th>
                    <th className="px-4 py-3 font-semibold">Department</th>
                    <th className="px-4 py-3 font-semibold">Assigned</th>
                    <th className="px-4 py-3 font-semibold">Completed</th>
                    <th className="px-4 py-3 font-semibold">Pending</th>
                    <th className="px-4 py-3 font-semibold">Overdue</th>
                    <th className="px-4 py-3 font-semibold">Avg Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((e) => (
                    <tr key={e.id} data-testid={`report-employee-${e.id}`} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{e.name}<span className="ml-2 text-xs font-normal text-slate-400">{e.role}</span></td>
                      <td className="px-4 py-2.5 text-slate-600">{e.department}</td>
                      <td className="px-4 py-2.5 font-data">{e.assigned}</td>
                      <td className="px-4 py-2.5 font-data text-emerald-700">{e.completed}</td>
                      <td className="px-4 py-2.5 font-data">{e.pending}</td>
                      <td className="px-4 py-2.5 font-data text-rose-600">{e.overdue}</td>
                      <td className="px-4 py-2.5 font-data">{e.avg_completion_hours != null ? `${e.avg_completion_hours}h` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="departments">
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3 font-semibold">Department</th>
                    <th className="px-4 py-3 font-semibold">Total</th>
                    <th className="px-4 py-3 font-semibold">Completed</th>
                    <th className="px-4 py-3 font-semibold">Pending</th>
                    <th className="px-4 py-3 font-semibold">Overdue</th>
                    <th className="px-4 py-3 font-semibold">Pending Review</th>
                    <th className="px-4 py-3 font-semibold">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.departments.map((d) => (
                    <tr key={d.department} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{d.department}</td>
                      <td className="px-4 py-2.5 font-data">{d.total}</td>
                      <td className="px-4 py-2.5 font-data text-emerald-700">{d.completed}</td>
                      <td className="px-4 py-2.5 font-data">{d.pending}</td>
                      <td className="px-4 py-2.5 font-data text-rose-600">{d.overdue}</td>
                      <td className="px-4 py-2.5 font-data text-amber-600">{d.pending_review}</td>
                      <td className="px-4 py-2.5 font-data">{d.completion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="managers">
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3 font-semibold">Manager</th>
                    <th className="px-4 py-3 font-semibold">Team Size</th>
                    <th className="px-4 py-3 font-semibold">Team Tasks</th>
                    <th className="px-4 py-3 font-semibold">Completed</th>
                    <th className="px-4 py-3 font-semibold">Overdue</th>
                    <th className="px-4 py-3 font-semibold">Pending Review</th>
                    <th className="px-4 py-3 font-semibold">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.managers.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{m.name}</td>
                      <td className="px-4 py-2.5 font-data">{m.team_size}</td>
                      <td className="px-4 py-2.5 font-data">{m.team_tasks}</td>
                      <td className="px-4 py-2.5 font-data text-emerald-700">{m.completed}</td>
                      <td className="px-4 py-2.5 font-data text-rose-600">{m.overdue}</td>
                      <td className="px-4 py-2.5 font-data text-amber-600">{m.pending_review}</td>
                      <td className="px-4 py-2.5 font-data">{m.completion_rate}%</td>
                    </tr>
                  ))}
                  {data.managers.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No manager data available.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
